/**
 * MontaTech v2 - StatusBar Component
 * 
 * Barra de status compacta com seletor de perfil customizado
 */

import { useState, useRef, useEffect } from 'react';
import { Cloud, HardDrive, Maximize2, Settings, ChevronDown, Check } from 'lucide-react';
import type { CanvasTransform, MontaTechProfile } from '../types';
import { usePing, type PingStatus } from '../hooks/usePing';

const PING_DOT_COLORS: Record<PingStatus, string> = {
  unknown: 'bg-gray-600',
  online:  'bg-green-400',
  slow:    'bg-amber-400',
  offline: 'bg-red-500',
};

const PING_TEXT_COLORS: Record<PingStatus, string> = {
  unknown: 'text-gray-500',
  online:  'text-green-400',
  slow:    'text-amber-400',
  offline: 'text-red-400',
};

interface StatusBarProps {
  selectedProfileName: string;
  isLocalProfile: boolean;
  profiles: MontaTechProfile[];
  activeProfiles: MontaTechProfile[];
  apiProfiles: MontaTechProfile[];
  localProfiles: MontaTechProfile[];
  transform: CanvasTransform;
  toolVersion: number | null;
  onSelectProfile: (name: string) => void;
  onOpenSettings: () => void;
  onZoomReset: () => void;
  onPopout?: () => void;
}

export function StatusBar({
  selectedProfileName,
  isLocalProfile,
  activeProfiles = [],
  apiProfiles = [],
  localProfiles = [],
  onSelectProfile,
  onOpenSettings,
  onZoomReset,
}: StatusBarProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const ping = usePing();

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleSelect = (name: string) => {
    onSelectProfile(name);
    setIsDropdownOpen(false);
  };

  return (
    <div className="flex items-center gap-2 px-2.5 py-2 border-b border-navy-700 bg-navy-800/50">
      {/* Left: Ping */}
      <div className="flex shrink-0 items-center">
        {/* Ping indicator */}
        <div
          className={`flex h-8 min-w-[48px] items-center gap-1.5 rounded-md px-1 text-[11px] font-medium ${PING_TEXT_COLORS[ping.status]}`}
          title={
            ping.status === 'online' ? `Backend online (${ping.latencyMs}ms)` :
            ping.status === 'slow' ? `Backend lento (${ping.latencyMs}ms)` :
            ping.status === 'offline' ? 'Backend offline' :
            'Verificando conexão...'
          }
        >
          <span className={`h-2 w-2 rounded-full ${PING_DOT_COLORS[ping.status]}`} />
          <span>
            {ping.latencyMs !== null ? `${ping.latencyMs}ms` : '--'}
          </span>
        </div>
      </div>

      {/* Center: Profile selector (custom dropdown) */}
      <div className="flex min-w-0 flex-1 items-center gap-1" ref={dropdownRef}>
        <div className="relative min-w-0 flex-1">
          {/* Trigger button */}
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex h-8 w-full min-w-0 items-center gap-2 rounded-md border border-navy-600 bg-navy-700 px-2.5 text-xs text-gray-200 transition-colors hover:bg-navy-600"
          >
            {isLocalProfile ? (
              <HardDrive className="h-3.5 w-3.5 shrink-0 text-amber-400" />
            ) : (
              <Cloud className="h-3.5 w-3.5 shrink-0 text-blue-400" />
            )}
            <span className="min-w-0 flex-1 truncate text-left">{selectedProfileName || 'Selecionar'}</span>
            <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown menu */}
          {isDropdownOpen && (
            <div className="absolute left-0 right-0 top-full z-50 mt-1 min-w-[220px] max-h-60 overflow-y-auto rounded-lg border border-navy-600 bg-navy-800 py-1 shadow-xl">
              {/* API Profiles */}
              {apiProfiles.some(p => p.enabled !== false) && (
                <>
                  <div className="px-2.5 py-1.5 text-[10px] text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                    <Cloud className="w-3 h-3" />
                    Servidor
                  </div>
                  {apiProfiles.filter(p => p.enabled !== false).map(p => (
                    <button
                      key={`api-${p.name}`}
                      onClick={() => handleSelect(p.name)}
                      className={`w-full px-2.5 py-1.5 text-xs text-left flex items-center gap-2 hover:bg-navy-700 transition-colors ${
                        selectedProfileName === p.name ? 'text-blue-400 bg-navy-700/50' : 'text-gray-300'
                      }`}
                    >
                      <span className="flex-1 truncate">{p.name}</span>
                      {selectedProfileName === p.name && <Check className="w-3 h-3 text-blue-400 shrink-0" />}
                    </button>
                  ))}
                </>
              )}

              {/* Separator */}
              {apiProfiles.some(p => p.enabled !== false) && localProfiles.some(p => p.enabled !== false) && (
                <div className="my-1 border-t border-navy-700" />
              )}

              {/* Local Profiles */}
              {localProfiles.some(p => p.enabled !== false) && (
                <>
                  <div className="px-2.5 py-1.5 text-[10px] text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                    <HardDrive className="w-3 h-3" />
                    Locais
                  </div>
                  {localProfiles.filter(p => p.enabled !== false).map(p => (
                    <button
                      key={`local-${p.name}`}
                      onClick={() => handleSelect(p.name)}
                      className={`w-full px-2.5 py-1.5 text-xs text-left flex items-center gap-2 hover:bg-navy-700 transition-colors ${
                        selectedProfileName === p.name ? 'text-amber-400 bg-navy-700/50' : 'text-gray-300'
                      }`}
                    >
                      <span className="flex-1 truncate">{p.name}</span>
                      {selectedProfileName === p.name && <Check className="w-3 h-3 text-amber-400 shrink-0" />}
                    </button>
                  ))}
                </>
              )}
              {activeProfiles.length === 0 && (
                <div className="px-2.5 py-2 text-[11px] text-amber-300">
                  Nenhum perfil ativo disponível.
                </div>
              )}
            </div>
          )}
        </div>
        
        <button
          onClick={onOpenSettings}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-navy-700 hover:text-gray-200"
          title="Configurações"
        >
          <Settings className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Right: View controls */}
      <div className="flex shrink-0 items-center">
        <button
          onClick={onZoomReset}
          className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-navy-700 hover:text-gray-200"
          title="Reset zoom"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
        {/*
          Docker separado oculto por decisao de UI.
          Para reativar: renderizar um botao chamando `onPopout`.
        */}
      </div>
    </div>
  );
}
